/**
 * Login — email + password exchanged for an admin JWT.
 *
 * The password field is a real password input (never echoed, never logged); on success
 * the session is stored and the operator lands on the page they were headed for. A
 * failed sign-in shows the API's safe message, nothing more.
 *
 * "Remember me for 30 days" does two things, one on each side of the wire. Locally it moves the
 * session from `sessionStorage` to `localStorage`, so it survives closing the tab; on the API it
 * asks for a rotating remember-me lineage, which is what actually keeps the operator signed in
 * past the access token's hour. Left unticked, the behaviour is exactly what it has always been:
 * tab-scoped, and gone when the tab closes.
 *
 * The hint under the box says which of the two the operator is choosing, in the terms that
 * matter on the machine they are sitting at. See `api/session.ts` for the full trade-off.
 */
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { USING_MOCK } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { REMEMBER_ME_DAYS } from '../api/session.js';
import { useAuth } from '../auth/AuthContext.js';
import { Button, Field, Input } from '../components/ui.js';
import { IconCheck } from '../components/icons.js';
import { useBrand } from '../branding/BrandProvider.js';

interface FromState {
  from?: { pathname?: string };
}

export function LoginPage(): JSX.Element {
  const { login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  /*
   * Ticked by default.
   *
   * The machinery for a thirty-day session was complete and correct - a rotating lineage, reuse
   * detection, a family deadline that a refresh renews the token against but never extends -
   * and it did nothing, because nobody ticked the box. An operator signed in, came back the
   * next morning, and signed in again, which is indistinguishable from the feature not
   * existing.
   *
   * The default is a judgement about who uses this console: the people running the business,
   * on their own machines, several times a day. The safe-on-a-shared-computer default was
   * protecting a case that is rare here at the cost of the case that is constant.
   *
   * It is a real trade and the hint below states it plainly rather than burying it: a
   * remembered session lives in `localStorage`, survives closing the tab, and is one unticked
   * box away for anybody who needs the old behaviour.
   */
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const dest = (location.state as FromState | null)?.from?.pathname ?? '/';

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login({ email, password, rememberMe: remember }, remember);
      navigate(dest, { replace: true });
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <aside className="login__aside">
        <div className="brandmark" style={{ color: '#fff', fontSize: 18 }}>
          <span className="brandmark__glyph">{initialOf(brand.platformName)}</span>{' '}
          {brand.platformName}
        </div>
        <div>
          <div className="login__tagline">Sell crypto. Settle naira in seconds.</div>
          <div className="login__points">
            {[
              'Turn features on or off, and run gift cards',
              'Choose which payment provider is used, and check the books balance',
              'ID checks, spending limits, and customer controls',
            ].map((p) => (
              <div className="login__point" key={p}>
                <span className="login__check">
                  <IconCheck size={13} />
                </span>
                {p}
              </div>
            ))}
          </div>
        </div>
        <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 12.5 }}>
          Operator console · Restricted access · All actions are audited
        </div>
      </aside>

      <div className="login__main">
        <div className="login__card">
          <h1 style={{ fontSize: 24 }}>Sign in</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Use your operator credentials to continue.
          </p>

          <form className="login__form" onSubmit={onSubmit}>
            {error !== null && <div className="form-error">{error}</div>}
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                autoComplete="username"
                placeholder="you@genex.pay"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>

            {/*
              A real checkbox rather than the console's `Switch`. A switch reads as a setting
              that takes effect the moment it moves; this one only means anything when the
              form is submitted, and a control that looks live but is not is the sort of thing
              an operator later swears they turned on.
            */}
            <div className="login__remember">
              <label className="checkline" htmlFor="remember">
                <input
                  id="remember"
                  type="checkbox"
                  checked={remember}
                  disabled={busy}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                <span className="checkline__label">
                  Remember me for {REMEMBER_ME_DAYS} days
                </span>
              </label>
              <p className="checkline__hint">
                {remember
                  ? 'Your session is kept on this device, not just this tab, and renews itself for up to 30 days. Signing out ends it everywhere. Untick this on a shared computer.'
                  : 'Off, your session lives in this tab only and ends when you close it or after an hour. This is the safe choice on a shared computer.'}
              </p>
            </div>

            <Button type="submit" variant="primary" block loading={busy}>
              Sign in
            </Button>
          </form>

          {USING_MOCK && (
            <div className="login__hint">
              <strong>Demo mode.</strong> No API configured. Sign in with{' '}
              <code>superadmin@genex.pay</code> (or <code>compliance@</code>, <code>finance@</code>,{' '}
              <code>support@</code>) and password <code>genex-admin</code>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The lettermark shown when no logo has been uploaded.
 *
 * Strips anything that is not a letter or digit, so "Acme Money" gives "A" rather than
 * whatever punctuation the name starts with, and a name that is entirely punctuation still
 * yields something rather than an empty box.
 */
function initialOf(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9]/g, '');
  return (cleaned[0] ?? '?').toUpperCase();
}
